import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, COLOR_PALETTES } from '../contexts/ThemeContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Settings as SettingsIcon, Palette, Moon, Sun, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user } = useAuth();
  const { darkMode, toggleDarkMode, colorPalette, setColorPalette, palettes } = useTheme();

  const handlePaletteChange = (paletteKey) => {
    setColorPalette(paletteKey);
    toast.success(`Theme changed to ${palettes[paletteKey].name}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="settings-page">
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <SettingsIcon className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Settings</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">Appearance</h1>
            <p className="text-muted-foreground">Customize how SkinUse looks for you</p>
          </div>

          {/* Dark Mode Toggle */}
          <Card className="bg-card border-none shadow-card mb-6" data-testid="dark-mode-card">
            <CardHeader>
              <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                {darkMode ? <Moon className="w-5 h-5 text-gold" /> : <Sun className="w-5 h-5 text-gold" />}
                Dark Mode
              </h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {darkMode 
                      ? 'Dark mode is enabled. Easier on your eyes at night.' 
                      : 'Light mode is enabled. Bright and clean look.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Sun className={`w-4 h-4 ${!darkMode ? 'text-gold' : 'text-muted-foreground'}`} />
                  <Switch
                    checked={darkMode}
                    onCheckedChange={toggleDarkMode}
                    data-testid="dark-mode-toggle"
                  />
                  <Moon className={`w-4 h-4 ${darkMode ? 'text-gold' : 'text-muted-foreground'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Color Palette Selection */}
          <Card className="bg-card border-none shadow-card" data-testid="color-palette-card">
            <CardHeader>
              <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                <Palette className="w-5 h-5 text-gold" />
                Color Theme
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-6">
                Choose a color palette that suits your style. This will change the accent colors throughout the app.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(palettes).map(([key, palette]) => (
                  <button
                    key={key}
                    onClick={() => handlePaletteChange(key)}
                    className={`relative p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                      colorPalette === key 
                        ? 'border-foreground shadow-lg' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    data-testid={`palette-${key}`}
                  >
                    {colorPalette === key && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-foreground rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-background" />
                      </div>
                    )}
                    
                    {/* Color Preview */}
                    <div className="flex gap-1 mb-3">
                      <div 
                        className="w-6 h-6 rounded-full" 
                        style={{ backgroundColor: palette.gold }}
                      />
                      <div 
                        className="w-6 h-6 rounded-full" 
                        style={{ backgroundColor: palette.primary }}
                      />
                      <div 
                        className="w-6 h-6 rounded-full" 
                        style={{ backgroundColor: palette.accent }}
                      />
                    </div>
                    
                    <p className="text-sm font-medium">{palette.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card className="bg-card border-none shadow-card mt-6" data-testid="preview-card">
            <CardHeader>
              <h3 className="font-serif text-xl font-semibold">Preview</h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button className="btn-gold">Primary Button</Button>
                  <Button variant="outline">Outline Button</Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-gold/10 text-gold rounded-sm text-sm font-semibold">
                    Gold Badge
                  </span>
                  <span className="px-3 py-1 bg-secondary text-foreground rounded-sm text-sm">
                    Secondary Badge
                  </span>
                </div>
                <p className="text-sm">
                  This is how your <span className="text-gold font-semibold">accent color</span> will appear throughout the app.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
